/**
 * THE BRACKET CONTROL.
 *
 * One compact row: a rounded segmented bar of six presets with the selected
 * one drawn as a filled pill, then `Custom` as a real disclosure, then the
 * resolved range echoed on the right so the reader can SEE which boundary
 * they got.
 *
 * IT IS CHROME. There is no Signal Amber in it at any state. Amber marks one
 * thing in this product — money at risk right now — and a control the reader
 * touches twenty times an hour would spend that signal by lunchtime. The
 * filled pill is the same `--n-800` on `--bone` the existing `.seg__btn`
 * selection already uses, so the console has ONE selection idiom.
 *
 * `Custom` is a disclosure and not a seventh pill because it does not name a
 * range — it asks for one. Its caret is the stylesheet's existing
 * `details > summary::before` border triangle: ▾ is in none of the three
 * brand faces, and a glyph that falls back to an OS font at a different
 * x-height is the bug the font subset exists to prevent.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import { BRACKET_LABEL, BRACKETS, DEFAULT_BRACKET, useBracket, type Bracket } from "./bracket";
import { fromLocalInput, toLocalInput } from "./time";

export function BracketBar() {
  const { bracket, from, to, range, setBracket, setCustom } = useBracket();
  const details = useRef<HTMLDetailsElement>(null);
  const tabs = useRef<HTMLDivElement>(null);
  const [f, setF] = useState(() => toLocalInput(from));
  const [t, setT] = useState(() => toLocalInput(to));
  const [err, setErr] = useState<string | null>(null);

  // The store is shared across five surfaces: a range set on LEDGER must show
  // up in these fields when the reader lands on DECK.
  useEffect(() => {
    setF(toLocalInput(from));
    setT(toLocalInput(to));
  }, [from, to]);

  const custom = bracket === "custom";

  /** Roving tabindex: one stop for the whole group, arrows move within it. */
  const onKey = (e: KeyboardEvent<HTMLElement>, i: number) => {
    const d =
      e.key === "ArrowRight"
        ? 1
        : e.key === "ArrowLeft"
          ? -1
          : e.key === "Home"
            ? -i
            : e.key === "End"
              ? BRACKETS.length - 1 - i
              : 0;
    if (!d) return;
    e.preventDefault();
    const next = Math.min(BRACKETS.length - 1, Math.max(0, i + d));
    setBracket(BRACKETS[next]!);
    const el = tabs.current?.querySelectorAll<HTMLButtonElement>(".bracket__btn")[next];
    el?.focus();
  };

  const apply = () => {
    const since = fromLocalInput(f);
    const until = fromLocalInput(t);
    if (!since && !until) {
      setErr("give a from, a to, or both");
      return;
    }
    if (since && until && since >= until) {
      setErr("from must be before to");
      return;
    }
    setErr(null);
    setCustom(since, until);
    if (details.current) details.current.open = false;
  };

  return (
    <div className="bracket" role="group" aria-label="time range">
      <div className="bracket__seg" role="tablist" aria-label="preset ranges" ref={tabs}>
        {BRACKETS.map((b, i) => {
          const sel = bracket === b;
          return (
            <button
              key={b}
              type="button"
              className="bracket__btn"
              role="tab"
              aria-selected={sel}
              tabIndex={sel || (custom && i === 0) ? 0 : -1}
              title={titleFor(b)}
              onClick={() => setBracket(b)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {BRACKET_LABEL[b]}
            </button>
          );
        })}
      </div>

      <details className="bracket__custom" data-selected={custom ? "true" : undefined} ref={details}>
        <summary title="an explicit from/to range; it wins over every preset">{BRACKET_LABEL.custom}</summary>
        <div className="bracket__range">
          <label className="bracket__field">
            <span>from</span>
            <input
              type="datetime-local"
              className="mono"
              value={f}
              step={60}
              onChange={(e) => setF(e.target.value)}
            />
          </label>
          <label className="bracket__field">
            <span>to</span>
            <input
              type="datetime-local"
              className="mono"
              value={t}
              step={60}
              onChange={(e) => setT(e.target.value)}
            />
          </label>
          <button type="button" className="bracket__apply" onClick={apply}>
            apply
          </button>
          <button
            type="button"
            className="bracket__clear"
            onClick={() => {
              setErr(null);
              setF("");
              setT("");
              setBracket(DEFAULT_BRACKET);
              if (details.current) details.current.open = false;
            }}
          >
            clear
          </button>
          {/* Always mounted so the panel cannot jump, and announced. */}
          <div className="bracket__err" role="alert" aria-live="polite">
            {err ?? ""}
          </div>
        </div>
      </details>

      <span className="bracket__scope mono">{range.label}</span>
    </div>
  );
}

/** The formula, one hop away from the default reading. */
function titleFor(b: Bracket): string {
  switch (b) {
    case "24h":
      return "the last 24 hours, tracking the clock";
    case "7d":
    case "30d":
    case "90d":
      return `the last ${b.replace("d", "")} days, tracking the clock`;
    case "ytd":
      return "1 January of the current year to now, in the timezone the console is displaying — flip LOCAL/UTC and the boundary moves";
    case "all":
      return "no lower bound and no upper bound; the page cap still applies";
    default:
      return "an explicit range";
  }
}
